import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { ThemeData, ThemesState } from '../reducer';
import { getClientAndResourceIds } from '../utils/helpers';
import { setResourcesData } from './sources';
import { setThumbnailsToUpdate } from './thumbnails';
import indesignEditorUtils from '../utils/indesignEditor';
import { createNotification } from '../../NotificationBar/actions';
import ApiUtils from '../../../utils/api';

export enum ThemesActionKeys {
	SET_THEMES = '@@editor/themes/SET_THEMES',
	SELECT_THEME = '@@editor/themes/SELECT_THEME',
	DUPLICATE_THEME = '@@editor/themes/DUPLICATE_THEME',
	REMOVE_THEME = '@@editor/themes/REMOVE_THEME',
	RENAME_THEME = '@@editor/themes/RENAME_THEME',
	CHANGE_THEME_COLOR = '@@editor/themes/CHANGE_THEME_COLOR',
}

interface SetThemesAction extends Action {
	type: ThemesActionKeys.SET_THEMES;
	themes: ThemesState;
}

interface SelectThemeAction extends Action {
	type: ThemesActionKeys.SELECT_THEME;
	id: string;
}

interface DuplicateThemeAction extends Action {
	type: ThemesActionKeys.DUPLICATE_THEME;
	id: string;
	theme: ThemeData;
}

interface RemoveThemeAction extends Action {
	type: ThemesActionKeys.REMOVE_THEME;
	id: string;
	newSelectedThemeId: string;
}

interface RenameThemeAction extends Action {
	type: ThemesActionKeys.RENAME_THEME;
	id: string;
	name: string;
}

interface ChangeThemeColorAction extends Action {
	type: ThemesActionKeys.CHANGE_THEME_COLOR;
	themeId: string;
	colorIndex: number;
	colorValue: string;
}

export type ThemesAction =
	SetThemesAction |
	SelectThemeAction |
	DuplicateThemeAction |
	RemoveThemeAction |
	RenameThemeAction |
	ChangeThemeColorAction;

export const setThemes = (themes: ThemesState): SetThemesAction => ({
	type: ThemesActionKeys.SET_THEMES,
	themes,
});

export const selectTheme = (id: string, shouldUpdateCss: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const action: SelectThemeAction = {
		type: ThemesActionKeys.SELECT_THEME,
		id,
	};
	dispatch(action);

	if (!shouldUpdateCss) {
		return;
	}

	const {
		editor: {
			resources: { themesCss },
			selectedThemeId,
		},
	} = getState();
	const modifiedThemesCss = indesignEditorUtils.getCssWithUpdatedThemes(themesCss as string, selectedThemeId as string);
	dispatch(saveThemesCss(modifiedThemesCss));
};

export const duplicateTheme = (themeId: string, name: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: { themes },
	} = getState();
	const { colors } = (themes as ThemesState)[themeId];
	const newThemeId = Date.now().toString();
	const newTheme: ThemeData = {
		id: newThemeId,
		name,
		colors,
	};

	const action: DuplicateThemeAction = {
		type: ThemesActionKeys.DUPLICATE_THEME,
		id: newThemeId,
		theme: newTheme,
	};
	dispatch(action);

	const {
		editor: {
			resources: { themesCss },
		},
	} = getState();
	const modifiedThemesCss = indesignEditorUtils.getCssWithDuplicatedTheme(themesCss as string, themeId, newTheme);
	dispatch(saveThemesCss(modifiedThemesCss));
};

export const removeTheme = (themeId: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: { themes },
	} = getState();

	const themeIds = Object.keys(themes as ThemesState).filter(id => id !== themeId);
	const newSelectedThemeId = themeIds[0];

	const action: RemoveThemeAction = {
		type: ThemesActionKeys.REMOVE_THEME,
		id: themeId,
		newSelectedThemeId,
	};
	dispatch(action);

	const {
		editor: {
			resources: { themesCss },
		},
	} = getState();
	const modifiedThemesCss = indesignEditorUtils.getCssWithoutRemovedTheme(themesCss as string, themeId, newSelectedThemeId);
	dispatch(saveThemesCss(modifiedThemesCss));
};

export const renameTheme = (themeId: string, themeName: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const action: RenameThemeAction = {
		type: ThemesActionKeys.RENAME_THEME,
		id: themeId,
		name: themeName,
	};
	dispatch(action);

	const {
		editor: {
			resources: { themesCss },
		},
	} = getState();
	const modifiedThemesCss = indesignEditorUtils.getCssWithRenamedTheme(themesCss as string, themeId, themeName);
	dispatch(saveThemesCss(modifiedThemesCss));
};

export const changeThemeColor = (themeId: string, colorIndex: number, colorValue: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const action: ChangeThemeColorAction = {
		type: ThemesActionKeys.CHANGE_THEME_COLOR,
		themeId,
		colorIndex,
		colorValue,
	};
	dispatch(action);

	const {
		editor: {
			resources: { themesCss },
		},
	} = getState();
	const modifiedThemesCss = indesignEditorUtils.getCssWithUpdatedThemeColor(themesCss as string, themeId, colorIndex, colorValue);
	dispatch(saveThemesCss(modifiedThemesCss));
};

const saveThemesCss = (themesCss: string) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { clientId, tempGId: resourceId } = getClientAndResourceIds();

		dispatch(setResourcesData({
			themesCss,
		}));
		const thumbnailsToUpdate = await ApiUtils.saveIndesignEditorThemesCss(clientId, resourceId, themesCss);
		dispatch(setThumbnailsToUpdate(thumbnailsToUpdate));
	} catch (error) {
		dispatch(createNotification('error.save_editor_data_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/themes.ts